package co.maytes.standupbot.dashboard.features.templates

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Composable
fun TemplatePresetStrip(
    activePresetId: String?,
    onPick: (TemplatePreset) -> Unit,
    onClear: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = Spacing.lg),
        verticalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.styleModifier { property("min-width", "0") }) {
                SpanText(
                    text = "Start from a preset",
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary),
                )
                SpanText(
                    text = "Pick a baseline prompt and tweak from there. Preview updates live on the right.",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textSecondary),
                )
            }
            if (activePresetId != null) {
                SpanText(
                    text = "Clear",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textSecondary)
                        .cursor(Cursor.Pointer)
                        .onClick { onClear() }
                        .styleModifier { property("text-decoration", "underline") },
                )
            }
        }
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("overflow-x", "auto")
                    property("padding", "12px 0 28px")
                    property("margin", "-12px 0 -24px")
                },
            horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        ) {
            TemplatePresets.all.forEach { preset ->
                PresetCard(
                    preset = preset,
                    active = preset.id == activePresetId,
                    onClick = { onPick(preset) },
                )
            }
        }
    }
}

@Composable
private fun PresetCard(
    preset: TemplatePreset,
    active: Boolean,
    onClick: () -> Unit,
) {
    val bg = if (active) Palette.accentSubtle else Palette.surface
    val borderColor = if (active) Palette.accent else Palette.border
    Card(
        modifier = Modifier
            .backgroundColor(bg)
            .padding(Spacing.md)
            .gap(Spacing.xs)
            .cursor(Cursor.Pointer)
            .pressable()
            .onClick { onClick() }
            .styleModifier {
                property("border", "1px solid ${borderColor.cssRgb()}")
                property("min-width", "220px")
                property("max-width", "260px")
                property("flex", "0 0 auto")
            },
        padded = false,
        interactive = true,
    ) {
        SpanText(
            text = preset.emoji,
            modifier = Modifier.fontSize(24.px),
        )
        SpanText(
            text = preset.name,
            modifier = Modifier
                .fontSize(Typography.body.size)
                .fontWeight(600)
                .color(Palette.textPrimary),
        )
        SpanText(
            text = preset.tagline,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(if (active) Palette.accent else Palette.textSecondary)
                .fontWeight(500),
        )
        SpanText(
            text = preset.description,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textTertiary)
                .styleModifier { property("line-height", "1.5") },
        )
    }
}
